import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Edit2,
  Plus,
  X,
  Save,
  Check,
  CircleMinus,
  CircleCheck,
  Shield,
  AlertTriangle,
  FileText,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Search,
  Link2,
  Unlink,
  Lock,
} from "lucide-react";
import RiskVisualization from "../../risk/RiskVisualization";
import {
  governanceContractsService,
  governanceContractTermsService,
  termSopLinksService,
  GovernanceContract,
  GovernanceContractTerm,
  TermSopLink,
  TermAvailableSop,
} from "../../../services/governanceService";
import GovernanceProcesses from "./GovernanceProcesses";
import thirdPartiesService, {
  ThirdParty,
} from "../../../services/thirdPartiesService";
import FormControl from "../../common/FormControl";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import Pagination from "../../common/Pagination";
import Toast, { ToastMessage } from "../../common/Toast";
import { confirmWarning, confirmActive, confirmDelete } from "../../common/confirm";
import NoDataFound from "../../common/NoDataFound";
import StatusSwitch from "../../common/StatusSwitch";
import Skeleton from "../../common/Skeleton";
import Badge from "../../common/Badge";
import { statusVariant, severityBadgeCss } from "../../../utils/statusVariant";
import { usePermission, NOT_AUTHORISED_MSG } from "../../../contexts/PermissionContext";
import ApprovalDot, { WorkflowState } from "../../common/ApprovalDot";
import WorkflowModal from "../../common/WorkflowModal";
import { useFieldValidation } from "../../../validation/useFieldValidation";
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from "../../../validation/ValidatedFields";
import { isUniqueLocal } from "../../../validation/uniqueness";
import { useEntitySettings } from "../../../contexts/EntitySettingsContext";

const WF_KIND = "gov_agreement";

// --- Types ---

type ContractType = "VENDOR" | "CUSTOMER" | "AGENCY";

interface Contract {
  id: number;
  contract_id: string;
  title: string;
  party_name: string;
  type: ContractType;
  status: "DRAFT" | "ACTIVE" | "EXPIRED" | "TERMINATED";
  start_date: string;
  end_date: string;
  value: string;
  owner: string;
  is_active: number;
  workflow_state: WorkflowState;
}

interface ContractTerm {
  id: number;
  contract_id: number;
  clause_reference: string;
  title: string;
  description: string;
  criticality: "HIGH" | "MEDIUM" | "LOW";
  is_active: number;
}

interface Control {
  id: number;
  term_id: number;
  code: string;
  name: string;
  description: string;
  type: "PREVENTIVE" | "DETECTIVE" | "CORRECTIVE";
}

interface Risk {
  id: number;
  control_id: number;
  description: string;
  severity: "HIGH" | "MEDIUM" | "LOW";
}

interface Attribute {
  id: number;
  control_id: number;
  name: string;
  value: string;
}

// --- Section definitions ---

const SECTIONS: { key: ContractType; label: string }[] = [
  { key: "VENDOR", label: "Vendors" },
  { key: "CUSTOMER", label: "Customers" },
  { key: "AGENCY", label: "Agencies" },
];

// --- Field Mapping Helpers ---

const mapApiToContract = (item: GovernanceContract): Contract => ({
  id: item.id,
  // System code (CNT-N), assigned on approve. Empty until then.
  contract_id: item.code || "",
  title: item.title,
  party_name: item.party_name,
  type: (item.contract_type || "VENDOR") as ContractType,
  status: (item.contract_status || "DRAFT") as any,
  start_date: item.start_date || "",
  end_date: item.end_date || "",
  value: item.contract_value || "",
  owner: item.owner || "",
  is_active: item.is_active,
  workflow_state: ((item as any).workflow_state || "draft") as WorkflowState,
});

const mapApiToTerm = (item: GovernanceContractTerm): ContractTerm => ({
  id: item.id,
  contract_id: item.contract_id,
  clause_reference: item.clause_reference || "",
  title: item.title,
  description: item.description || "",
  criticality: (item.criticality || "MEDIUM") as any,
  is_active: item.is_active,
});

// --- Sub-Components for 3-Column Layout ---

const RisksColumn = ({
  selectedControl,
  risks,
  onRiskClick,
}: {
  selectedControl: Control | null;
  risks: Risk[];
  onRiskClick: (risk: Risk) => void;
}) => {
  const relevantRisks = risks.filter(
    (r) => r.control_id === selectedControl?.id,
  );

  if (!selectedControl) {
    return (
      <div className="flex flex-col h-full bg-gray-50 border-r border-gray-200 items-center justify-center p-8 text-center text-gray-400">
        <AlertTriangle size={32} className="opacity-20 mb-2" />
        <p className="text-sm">Select a Control to view mitigated Risks.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50 border-r border-gray-200 min-h-0">
      <div className="h-16 px-4 border-b border-gray-200 bg-white shadow-sm z-10 shrink-0 flex items-center">
        <div className="flex items-center gap-2 text-gray-700 font-bold text-lg">
          <AlertTriangle className="text-amber-500" size={20} />
          <h2>Mitigated Risks</h2>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <div
          className={`space-y-3 transition-all duration-300 ${selectedControl ? "bg-orange-50/50 p-3 rounded-xl border-2 border-orange-200" : ""}`}
        >
          {relevantRisks.length === 0 ? (
            <div className="text-center py-6 text-gray-400 text-sm italic">
              No risks linked.
            </div>
          ) : (
            relevantRisks.map((risk) => (
              <div
                key={risk.id}
                onClick={() => onRiskClick(risk)}
                className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all cursor-pointer hover:border-orange-300 group"
              >
                <div className="flex justify-between items-start mb-1">
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded border ${
                      risk.severity === "HIGH"
                        ? "bg-red-50 text-red-600 border-red-100"
                        : risk.severity === "MEDIUM"
                          ? "bg-amber-50 text-amber-600 border-amber-100"
                          : "bg-blue-50 text-blue-600 border-blue-100"
                    }`}
                  >
                    {risk.severity}
                  </span>
                  <ArrowRight
                    size={12}
                    className="text-gray-300 group-hover:text-orange-500 transition-colors opacity-0 group-hover:opacity-100"
                  />
                </div>
                <p className="text-xs text-gray-700 leading-snug">
                  {risk.description}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const ControlsColumn = ({
  term,
  controls,
  selectedControlId,
  onSelectControl,
  onAddControl,
}: any) => {
  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200 min-h-0">
      <div className="h-16 px-4 border-b border-gray-200 bg-white shadow-sm z-10 shrink-0 flex justify-between items-center">
        <div className="flex items-center gap-2 text-gray-700 font-bold text-lg">
          <Shield className="text-blue-600" size={20} />
          <h2>Controls</h2>
        </div>
        <Button variant="fill" icon={Plus} onClick={onAddControl} data-testid="BTN_contract_term_add_control">
          Create
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 bg-slate-50/30">
        <div className="space-y-3">
          {controls.length === 0 ? (
            <div className="text-center py-8 text-gray-400 italic">
              No controls defined. Add one to start.
            </div>
          ) : (
            controls.map((control: Control) => (
              <div
                key={control.id}
                onClick={() => onSelectControl(control)}
                data-testid={`BTN_contract_control_${control.id}`}
                className={`relative p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedControlId === control.id
                    ? "bg-white border-blue-500 shadow-md ring-1 ring-blue-100"
                    : "bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm"
                }`}
              >
                {selectedControlId === control.id && (
                  <div className="absolute -left-2 top-1/2 -translate-y-1/2 bg-orange-500 text-white p-0.5 rounded-full shadow-sm z-20">
                    <ChevronLeft size={12} />
                  </div>
                )}
                <div className="flex justify-between items-start mb-1">
                  <span className=" text-xs font-bold text-gray-500">
                    {control.code}
                  </span>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded border ${
                      control.type === "PREVENTIVE"
                        ? "bg-green-50 text-green-600 border-green-100"
                        : control.type === "DETECTIVE"
                          ? "bg-purple-50 text-purple-600 border-purple-100"
                          : "bg-orange-50 text-orange-600 border-orange-100"
                    }`}
                  >
                    {control.type}
                  </span>
                </div>
                <h4 className="text-sm font-semibold text-gray-800 mb-1">
                  {control.name}
                </h4>
                <p className="text-xs text-gray-600 line-clamp-2">
                  {control.description}
                </p>
                {selectedControlId === control.id && (
                  <div className="absolute -right-2 top-1/2 -translate-y-1/2 bg-blue-500 text-white p-0.5 rounded-full shadow-sm z-20">
                    <ChevronRight size={12} />
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const AttributesColumn = ({
  selectedControl,
  attributes,
}: {
  selectedControl: Control | null;
  attributes: Attribute[];
}) => {
  const relevantAttributes = attributes.filter(
    (a) => a.control_id === selectedControl?.id,
  );

  if (!selectedControl) {
    return (
      <div className="flex flex-col h-full bg-gray-50 items-center justify-center p-8 text-center text-gray-400">
        <FileText size={32} className="opacity-20 mb-2" />
        <p className="text-sm">Select a Control to view Implementations.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50 min-h-0">
      <div className="h-16 px-4 border-b border-gray-200 bg-white shadow-sm z-10 shrink-0 flex items-center">
        <div className="flex items-center gap-2 text-gray-700 font-bold text-lg">
          <FileText className="text-purple-600" size={20} />
          <h2>Implementations</h2>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <div
          className={`space-y-3 transition-all duration-300 ${selectedControl ? "bg-blue-50/50 p-3 rounded-xl border-2 border-blue-200" : ""}`}
        >
          {relevantAttributes.length === 0 ? (
            <div className="text-center py-6 text-gray-400 text-sm italic">
              No implementations defined.
            </div>
          ) : (
            relevantAttributes.map((attr) => (
              <div
                key={attr.id}
                className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm flex justify-between items-center hover:shadow-md transition-shadow"
              >
                <span className="text-xs font-semibold text-gray-600">
                  {attr.name}
                </span>
                <span className="text-xs text-gray-800 bg-gray-50 px-2 py-1 rounded border border-gray-100">
                  {attr.value}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// --- Component ---

const GovernanceContracts: React.FC = () => {
  const { allowed: canModify, loading: permLoading } = usePermission('GOV_AGREEMENTS', 'view+modify');
  const { allowed: canView, loading: viewLoading } = usePermission('GOV_AGREEMENTS', 'view');
  const { formatDate } = useEntitySettings();
  const [workflowRow, setWorkflowRow] = useState<Contract | null>(null);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [terms, setTerms] = useState<ContractTerm[]>([]);
  const [controls, setControls] = useState<Control[]>([]);
  const [risks] = useState<Risk[]>([]);
  const [attributes] = useState<Attribute[]>([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingContract, setTogglingContract] = useState(false);
  const [togglingTerm, setTogglingTerm] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [termsLoading, setTermsLoading] = useState(false);

  // Main table pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Terms table pagination
  const [termPage, setTermPage] = useState(1);
  const [termPageSize, setTermPageSize] = useState(10);

  const [typeFilter, setTypeFilter] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [activeFilter, setActiveFilter] = useState<'active' | 'inactive'>('active');
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingBasic, setEditingBasic] = useState(false);
  const contractFormRef = useRef<HTMLFormElement>(null);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(
    null,
  );
  const [formData, setFormData] = useState<Partial<Contract>>({});
  const [thirdParties, setThirdParties] = useState<ThirdParty[]>([]);

  const [editTab, setEditTab] = useState<"details" | "terms">("details");

  const [showTermFormModal, setShowTermFormModal] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState<ContractTerm | null>(null);
  const [termFormData, setTermFormData] = useState<Partial<ContractTerm>>({});
  const [isTermEditMode, setIsTermEditMode] = useState(false);

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [activeTermForConfig, setActiveTermForConfig] =
    useState<ContractTerm | null>(null);
  const [selectedControl, setSelectedControl] = useState<Control | null>(null);

  const [showRiskVisModal, setShowRiskVisModal] = useState(false);
  const [selectedRiskForVis, setSelectedRiskForVis] = useState<Risk | null>(
    null,
  );

  // SOP linkage at term level — mirrors policy→SOP UX from GovernancePoliciesModal
  const [expandedTermId, setExpandedTermId] = useState<number | null>(null);
  const [termLinkedSops, setTermLinkedSops] = useState<TermSopLink[]>([]);
  const [termAvailableSops, setTermAvailableSops] = useState<TermAvailableSop[]>([]);
  const [termLinksLoading, setTermLinksLoading] = useState(false);
  const [showTermLinkModal, setShowTermLinkModal] = useState(false);
  const [termLinkError, setTermLinkError] = useState<string | null>(null);
  const [sopProfileId, setSopProfileId] = useState<number | null>(null);

  // Separate validation hooks for the Agreement form and the Term sub-form
  // (distinct instances so their inline errors never collide).
  const vh = useFieldValidation();
  const vhTerm = useFieldValidation();

  const loadTermLinkedSops = useCallback(async (termId: number) => {
    try {
      setTermLinksLoading(true);
      const links = await termSopLinksService.getLinksForTerm(termId);
      setTermLinkedSops(links);
    } catch {
      setTermLinkedSops([]);
    } finally {
      setTermLinksLoading(false);
    }
  }, []);

  const loadTermAvailableSops = useCallback(
    async (termId: number, search?: string) => {
      try {
        setTermLinkError(null);
        const available = await termSopLinksService.getAvailableSops(termId, search);
        setTermAvailableSops(available);
      } catch (err: any) {
        setTermAvailableSops([]);
        setTermLinkError(err?.message || "Failed to load available SOPs");
      }
    },
    [],
  );

  const handleTermCardClick = (term: ContractTerm) => {
    if (expandedTermId === term.id) {
      setExpandedTermId(null);
      setTermLinkedSops([]);
      setShowTermLinkModal(false);
    } else {
      setExpandedTermId(term.id);
      loadTermLinkedSops(term.id);
      setShowTermLinkModal(false);
    }
  };

  const handleLinkSopToTerm = async (sopId: number) => {
    if (expandedTermId == null) return;
    try {
      await termSopLinksService.createLink(expandedTermId, sopId);
      await loadTermLinkedSops(expandedTermId);
      await loadTermAvailableSops(expandedTermId);
    } catch (err: any) {
      setToast({ type: "error", message: err.message || "Failed to link SOP" });
    }
  };

  const handleUnlinkSopFromTerm = async (sopId: number) => {
    if (expandedTermId == null) return;
    const ok = await confirmDelete({
      title: "Unlink this SOP?",
      text: "This will remove the SOP link from this term. You can re-link it later.",
      confirmText: "Yes, Unlink",
    });
    if (!ok) return;
    try {
      await termSopLinksService.removeLink(expandedTermId, sopId);
      await loadTermLinkedSops(expandedTermId);
    } catch (err: any) {
      setToast({ type: "error", message: err.message || "Failed to unlink SOP" });
    }
  };

  const loadContracts = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const data = await governanceContractsService.getAll({
        search: searchTerm || undefined,
      });
      setContracts(data.map(mapApiToContract));
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to load contracts",
      });
    } finally {
      setLoading(false);
    }
  }, [searchTerm, canView]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  useEffect(() => {
    thirdPartiesService
      .getAll({ status: "ACTIVE" })
      // Party dropdown must show approved + active only (mirrors /third-parties/dropdown).
      // Keep getAll (not /dropdown) because party_type is needed for eligibility filtering.
      .then((rows) => setThirdParties(rows.filter((p) => p.workflow_state === "approved")))
      .catch(() => setThirdParties([]));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, typeFilter, pageSize, statusFilter, activeFilter]);

  useEffect(() => {
    setTermPage(1);
  }, [termPageSize, selectedContract]);

  const loadTerms = useCallback(async (contractId: number) => {
    try {
      setTermsLoading(true);
      const data =
        await governanceContractTermsService.getByContract(contractId);
      setTerms(data.map(mapApiToTerm));
    } catch {
      setTerms([]);
    } finally {
      setTermsLoading(false);
    }
  }, []);

  // Load this contract's terms whenever the edit modal opens for it. Without this,
  // opening an agreement via the list row (handleRowClick) and switching to the
  // Terms tab showed no terms until one was created/edited — existing terms were
  // invisible (the only other loader, handleConfigure, was never wired up).
  useEffect(() => {
    if (showModal && isEditMode && selectedContract) {
      void loadTerms(selectedContract.id);
    }
  }, [showModal, isEditMode, selectedContract, loadTerms]);

  const filteredContracts = contracts.filter((c) => {
    const isActive = (c.is_active ?? 1) === 1;
    const matchesActive = activeFilter === 'active' ? isActive : !isActive;
    const matchesType = !typeFilter || c.type === typeFilter;
    const matchesStatus = !statusFilter || c.status === statusFilter;
    const matchesSearch =
      !searchTerm ||
      c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.party_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.contract_id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesActive && matchesType && matchesStatus && matchesSearch;
  });

  const totalRecords = filteredContracts.length;
  const paginatedContracts = filteredContracts.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  const activeTerms = terms.filter(
    (t) => t.contract_id === selectedContract?.id,
  );
  const totalTermRecords = activeTerms.length;
  const paginatedTerms = activeTerms.slice(
    (termPage - 1) * termPageSize,
    termPage * termPageSize,
  );

  const handleAddNew = () => {
    setSelectedContract(null);
    setFormData({
      title: "",
      party_name: "",
      type: "VENDOR",
      start_date: new Date().toISOString().split("T")[0],
      end_date: "",
      value: "",
      owner: "",
    });
    vh.reset();
    setIsEditMode(false);
    setShowModal(true);
  };

  const handleEditContract = (contract: Contract) => {
    setSelectedContract(contract);
    setFormData(contract);
    vh.reset();
    setIsEditMode(true);
    setEditingBasic(false);
    setEditTab("details");
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Engine format validation (109 name / 305 date) + 601 cross-field.
    const specs: any[] = [
      { name: "title", code: 109, value: formData.title, required: true },
      { name: "start_date", code: 305, value: formData.start_date },
      { name: "end_date", code: 305, value: formData.end_date },
    ];
    if (formData.start_date && formData.end_date) {
      specs.push({
        name: "end_date", code: 601, value: "x", required: true,
        additional: { from_date: formData.start_date, to_date: formData.end_date },
      });
    }
    const formatOk = vh.validateAll(specs);
    // Mandatory selects (no engine code — gate manually via setError).
    let selectsOk = true;
    if (!formData.type) { vh.setError("type", "Type is required"); selectsOk = false; }
    if (!formData.party_name) { vh.setError("party_name", "Party Name is required"); selectsOk = false; }
    // Uniqueness (entity-scoped, exclude self on edit).
    let uniqueOk = true;
    if (!isUniqueLocal(954, formData.title || "", contracts, isEditMode ? selectedContract?.id : undefined)) {
      vh.setError("title", "An agreement with this title already exists");
      uniqueOk = false;
    }
    if (!formatOk || !selectsOk || !uniqueOk) return;
    setSaving(true);
    try {
      const apiData: any = {
        title: (formData.title || "").trim(),
        party_name: (formData.party_name || "").trim(),
        contract_type: formData.type,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
      };
      if (isEditMode && selectedContract) {
        await governanceContractsService.update(selectedContract.id, {
          ...apiData,
          contract_status: formData.status,
        });
        setToast({
          type: "success",
          message: "Agreement updated successfully.",
        });
        setEditingBasic(false);
      } else {
        await governanceContractsService.create(apiData);
        setToast({
          type: "success",
          message: "Agreement created successfully.",
        });
        setShowModal(false);
      }
      await loadContracts();
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to save contract",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleContractActive = async () => {
    if (!selectedContract) return;
    const next = selectedContract.is_active ? 0 : 1;
    const confirmFn = selectedContract.is_active ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? "Reactivate this contract?" : "Inactivate this contract?",
      text: next
        ? "It will appear in active contract lists again."
        : "It will be hidden from active contract lists.",
      confirmText: next ? "Yes, Reactivate" : "Yes, Inactivate",
    });
    if (!ok) return;
    setTogglingContract(true);
    try {
      await governanceContractsService.update(selectedContract.id, {
        is_active: next,
      });
      setSelectedContract({ ...selectedContract, is_active: next });
      setToast({
        type: next ? "success" : "warning",
        message: next ? "Reactivated." : "Inactivated.",
      });
      await loadContracts();
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to update status",
      });
    } finally {
      setTogglingContract(false);
    }
  };

  const handleRowClick = (contract: Contract) => {
    setSelectedContract(contract);
    setFormData(contract);
    setIsEditMode(true);
    setEditingBasic(false);
    setEditTab("details");
    setShowModal(true);
  };

  const handleConfigure = async (e: React.MouseEvent, contract: Contract) => {
    e.stopPropagation();
    setSelectedContract(contract);
    setFormData(contract);
    setIsEditMode(true);
    setEditingBasic(false);
    setEditTab("terms");
    setShowModal(true);
    await loadTerms(contract.id);
  };

  const handleAddTerm = () => {
    setSelectedTerm(null);
    setTermFormData({
      clause_reference: "",
      title: "",
      description: "",
      criticality: "MEDIUM",
    });
    vhTerm.reset();
    setIsTermEditMode(false);
    setShowTermFormModal(true);
  };

  const handleEditTerm = (term: ContractTerm) => {
    setSelectedTerm(term);
    setTermFormData(term);
    vhTerm.reset();
    setIsTermEditMode(true);
    setShowTermFormModal(true);
  };

  const handleTermSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContract) return;
    // Engine format validation (109 title / 110 description).
    // clause_reference stays free text: code 501 forbids the dot in refs like "12.3".
    const ok = vhTerm.validateAll([
      { name: "term_title", code: 109, value: termFormData.title, required: true },
      { name: "term_description", code: 110, value: termFormData.description },
    ]);
    // Uniqueness within this contract's terms (activeTerms is already per-contract).
    let termUniqueOk = true;
    if (!isUniqueLocal(955, termFormData.title || "", activeTerms, isTermEditMode ? selectedTerm?.id : undefined)) {
      vhTerm.setError("term_title", "A term with this title already exists for this agreement");
      termUniqueOk = false;
    }
    if (!ok || !termUniqueOk) return;
    setSaving(true);
    try {
      const apiData: any = {
        contract_id: selectedContract.id,
        clause_reference: termFormData.clause_reference,
        title: termFormData.title,
        description: termFormData.description,
        criticality: termFormData.criticality,
      };
      if (isTermEditMode && selectedTerm) {
        await governanceContractTermsService.update(selectedTerm.id, apiData);
        setToast({ type: "success", message: "Term updated successfully." });
      } else {
        await governanceContractTermsService.create(apiData);
        setToast({ type: "success", message: "Term created successfully." });
      }
      setShowTermFormModal(false);
      await loadTerms(selectedContract.id);
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to save term",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleTermActive = async () => {
    if (!selectedTerm) return;
    const next = selectedTerm.is_active ? 0 : 1;
    const confirmFn = selectedTerm.is_active ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? "Reactivate this term?" : "Inactivate this term?",
      text: next
        ? "It will appear in active term lists again."
        : "It will be hidden from active term lists.",
      confirmText: next ? "Yes, Reactivate" : "Yes, Inactivate",
    });
    if (!ok) return;
    setTogglingTerm(true);
    try {
      await governanceContractTermsService.update(selectedTerm.id, {
        is_active: next,
      });
      setSelectedTerm({ ...selectedTerm, is_active: next });
      setToast({
        type: next ? "success" : "warning",
        message: next ? "Reactivated." : "Inactivated.",
      });
      if (selectedContract) await loadTerms(selectedContract.id);
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to update status",
      });
    } finally {
      setTogglingTerm(false);
    }
  };

  const handleOpenConfig = (term: ContractTerm) => {
    setActiveTermForConfig(term);
    setSelectedControl(null);
    setShowConfigModal(true);
  };

  const handleAddControl = () => {
    if (!activeTermForConfig) return;
    const newControl: Control = {
      id: Date.now(),
      term_id: activeTermForConfig.id,
      code: `CTRL-${Math.floor(Math.random() * 1000)}`,
      name: "New Control",
      description: "Description of the new control.",
      type: "PREVENTIVE",
    };
    setControls([...controls, newControl]);
    setSelectedControl(newControl);
  };

  const handleRiskClick = (risk: Risk) => {
    setSelectedRiskForVis(risk);
    setShowRiskVisModal(true);
  };


  // Approved agreements are locked — matches the Risk register / Masters pattern:
  // show the approved banner, disable all fields, and hide the Edit button until
  // the record is reopened via the workflow button.
  const contractIsApproved =
    isEditMode &&
    !!selectedContract &&
    selectedContract.workflow_state === "approved";
  const contractFieldsDisabled =
    isEditMode && (contractIsApproved || !editingBasic);

  const eligibleParties = thirdParties.filter(
    (p) => !formData.type || p.party_type === formData.type,
  );
  const partyInList =
    !!formData.party_name &&
    eligibleParties.some((p) => p.name === formData.party_name);
  const partyOptions = [
    ...eligibleParties.map((p) => ({ value: p.name, label: p.name })),
    ...(!partyInList && formData.party_name
      ? [{ value: formData.party_name, label: `${formData.party_name} (legacy)` }]
      : []),
  ];

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to agreements." />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col flex-1 min-w-0" style={{ height: "100%" }}>
        {/* Toolbar */}
        <div className="netbody-header">
          {/* Active/Inactive availability — iOS switch (separate from the
              lifecycle Status dropdown which filters Draft/Active/Expired/etc.) */}
          <StatusSwitch
            value={activeFilter === 'inactive' ? 'INACTIVE' : 'ACTIVE'}
            onChange={(next) => setActiveFilter(next === 'INACTIVE' ? 'inactive' : 'active')}
            noun="agreements"
            testId="SEL_contract_active_filter"
          />
          <div className="flex items-center gap-2 ml-auto">
            <FormControl.SearchSelect
              value={typeFilter}
              onChange={(v) => setTypeFilter(String(v ?? ''))}
              className="filter-status"
              data-testid="SEL_contract_type_filter"
              noSearchIcon
              options={[
                { value: '', label: 'All Types' },
                { value: 'VENDOR', label: 'Vendor' },
                { value: 'CUSTOMER', label: 'Customer' },
                { value: 'AGENCY', label: 'Agency' },
              ]}
            />
            <FormControl.SearchSelect
              value={statusFilter}
              onChange={(v) => setStatusFilter(String(v ?? ''))}
              className="filter-status"
              data-testid="SEL_contract_status_filter"
              noSearchIcon
              options={[
                { value: '', label: 'All Status' },
                { value: 'DRAFT', label: 'Draft' },
                { value: 'ACTIVE', label: 'Active' },
                { value: 'EXPIRED', label: 'Expired' },
                { value: 'TERMINATED', label: 'Terminated' },
              ]}
            />
            <FormControl.Input
              type="text"
              placeholder="Search agreements…"
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
              leadingIcon={<Search size={14} />}
              className="filter-search"
              data-testid="INP_contract_search"
            />
            <Button variant="fill" icon={Plus} onClick={handleAddNew} disabled={permLoading || !canModify} title={!canModify ? NOT_AUTHORISED_MSG : undefined} data-testid="BTN_contract_create">
              Create
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="netbody-content">
          <div className="scroll-container flex-1 overflow-hidden">
            <div className="table-list">
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th" style={{ width: 34 }} aria-label="Workflow status"></th>
                      <th className="table-list-th table-list-cell--sno">
                        S.No
                      </th>
                      <th className="table-list-th">Code</th>
                      <th className="table-list-th">Title</th>
                      <th className="table-list-th">Party Name</th>
                      <th className="table-list-th">Type</th>
                      <th className="table-list-th">Start Date</th>
                      <th className="table-list-th">End Date</th>
                      <th className="table-list-th">Status</th>
                    </tr>
                  </thead>
                  <tbody aria-busy={loading} aria-live="polite">
                    {!loading && filteredContracts.length === 0 && (
                      <tr>
                        <td colSpan={9} className="table-list-cell">
                          <NoDataFound
                            title="No Agreements Found"
                            message='Click "Create" to add one.'
                          />
                        </td>
                      </tr>
                    )}
                    {loading && <Skeleton.TableRows cols={['dot', '3xs', 'xs', 'md', 'sm', 'sm', 'xs', 'xs', 'pill']} />}
                    {!loading &&
                      paginatedContracts.map((contract, idx) => (
                        <tr
                          key={contract.id}
                          className="table-list-row"
                          onClick={() => handleRowClick(contract)}
                          data-testid={`BTN_contract_row_${contract.id}_open`}
                        >
                          <td className="table-list-cell" onClick={(e) => { e.stopPropagation(); setWorkflowRow(contract); }} title="Workflow — view timeline & act">
                            <ApprovalDot
                              state={(contract.workflow_state || 'draft') as WorkflowState}
                              onClick={() => setWorkflowRow(contract)}
                              title="Workflow — view timeline & act"
                            />
                          </td>
                          <td className="table-list-cell table-list-cell--sno">
                            {(page - 1) * pageSize + idx + 1}
                          </td>
                          <td className="table-list-cell">
                            {contract.contract_id}
                          </td>
                          <td className="table-list-cell">
                            <span className="table-list-cell-title cell-name-lock">
                              {contract.workflow_state === 'approved' && (
                                <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                              )}
                              {contract.title}
                            </span>
                          </td>
                          <td className="table-list-cell">
                            {contract.party_name}
                          </td>
                          <td className="table-list-cell">
                            <span className="text-xs text-gray-600">{contract.type}</span>
                          </td>
                          <td className="table-list-cell">
                            {formatDate(contract.start_date)}
                          </td>
                          <td className="table-list-cell">
                            {formatDate(contract.end_date)}
                          </td>
                          <td className="table-list-cell">
                            <Badge variant={statusVariant(contract.status)} size="sm">
                              {contract.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={page}
                pageSize={pageSize}
                totalRecords={totalRecords}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                recordLabel="agreements"
              />
            </div>
          </div>
        </div>

      {/* ── Contract Create / Edit Modal (tabbed in edit mode) ─────── */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        size="lg"
      >
        <Modal.Header>
          {isEditMode && selectedContract ? (
            <>
              {contractIsApproved ? "Viewing (locked) " : editingBasic ? "Editing " : "Viewing "}
              <span className="item-highlight">{selectedContract.title}</span>
              {selectedContract.contract_id && (
                <code className="ml-2 bg-slate-100 px-1.5 py-0.5 rounded text-xs text-gray-500">
                  {selectedContract.contract_id}
                </code>
              )}
            </>
          ) : (
            "Create Agreement"
          )}
        </Modal.Header>

        <Modal.Body className={isEditMode ? "p-0 flex flex-col" : undefined}>
          {/* Sticky tab bar — edit mode only */}
          {isEditMode && (
            <div
              className="app-modal-tabs"
              style={{
                position: "sticky",
                top: 0,
                zIndex: 5,
                background: "#fff",
                marginBottom: 0,
                paddingLeft: 16,
                paddingRight: 16,
                flexShrink: 0,
              }}
            >
              {(
                [
                  { key: "details", label: "Basic Details" },
                  { key: "terms", label: "Terms" },
                ] as const
              ).map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`app-modal-tab${editTab === t.key ? " app-modal-tab--active" : ""}`}
                  onClick={() => setEditTab(t.key)}
                  data-testid={`TAB_contract_${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* Per-tab panel — owns its own vertical scroll */}
          <div
            className={isEditMode ? "p-4" : undefined}
            style={isEditMode ? { flex: "1 1 auto", overflowY: "auto", minHeight: 0 } : undefined}
          >
            {/* ── Details tab (also the only content in create mode) ── */}
            {(!isEditMode || editTab === "details") && (
              <form
                ref={contractFormRef}
                id="contract-form"
                onSubmit={handleSubmit}
                className="grid grid-cols-12 gap-x-6 gap-y-4"
              >
                {contractIsApproved && (
                  <div className="approved-banner col-span-12">
                    <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                  </div>
                )}
                {isEditMode && (
                  <FormControl.Input
                    label="Code"
                    className="col-span-6"
                    value={formData.contract_id || ""}
                    disabled
                    helperText="Auto-generated"
                  />
                )}
                {isEditMode && (
                  <FormControl.SearchSelect
                    label="Status"
                    className="col-span-6"
                    data-testid="SEL_contract_status"
                    value={formData.status || "DRAFT"}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        status: String(value ?? "DRAFT") as any,
                      })
                    }
                    options={[
                      { value: "DRAFT", label: "Draft" },
                      { value: "ACTIVE", label: "Active" },
                      { value: "EXPIRED", label: "Expired" },
                      { value: "TERMINATED", label: "Terminated" },
                    ]}
                    disabled={contractFieldsDisabled}
                  />
                )}

                <ValidatedInput
                  vh={vh}
                  name="title"
                  code={109}
                  label="Title"
                  required
                  className="col-span-6"
                  data-testid="INP_contract_title"
                  value={formData.title || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  onAfterBlur={(v) => {
                    if (!isUniqueLocal(954, v, contracts, isEditMode ? selectedContract?.id : undefined)) {
                      vh.setError("title", "An agreement with this title already exists");
                    }
                  }}
                  placeholder="Contract title"
                  disabled={contractFieldsDisabled}
                />

                <FormControl.SearchSelect
                  label="Type"
                  className="col-span-6"
                  data-testid="SEL_contract_type"
                  value={formData.type || "VENDOR"}
                  onChange={(value) => {
                    setFormData({ ...formData, type: value as ContractType, party_name: "" });
                    vh.clearError("type");
                  }}
                  options={[
                    { value: "VENDOR", label: "Vendor" },
                    { value: "CUSTOMER", label: "Customer" },
                    { value: "AGENCY", label: "Agency" },
                  ]}
                  error={vh.errors.type}
                  disabled={contractFieldsDisabled}
                />

                <div className="col-span-6">
                  <FormControl.SearchSelect
                    label="Party Name"
                    required
                    data-testid="SEL_contract_party"
                    value={formData.party_name || ""}
                    onChange={(value) => {
                      setFormData({ ...formData, party_name: String(value ?? "") });
                      vh.clearError("party_name");
                    }}
                    options={partyOptions}
                    placeholder="— Select a party —"
                    error={vh.errors.party_name}
                    disabled={contractFieldsDisabled}
                  />
                  {eligibleParties.length === 0 && (
                    <p className="text-xs text-gray-400 mt-1">
                      No parties found. Add one under Governance → Masters → Contracts first.
                    </p>
                  )}
                </div>

                <ValidatedDatePicker
                  vh={vh}
                  name="start_date"
                  code={305}
                  label="Start Date"
                  className="col-span-6"
                  data-testid="INP_contract_start_date"
                  value={formData.start_date || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, start_date: e.target.value })
                  }
                  disabled={contractFieldsDisabled}
                />
                <ValidatedDatePicker
                  vh={vh}
                  name="end_date"
                  code={305}
                  label="End Date"
                  className="col-span-6"
                  data-testid="INP_contract_end_date"
                  value={formData.end_date || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, end_date: e.target.value })
                  }
                  disabled={contractFieldsDisabled}
                />
              </form>
            )}

            {/* ── Terms tab ─────────────────────────────────────────── */}
            {isEditMode && editTab === "terms" && (
              <div className="flex flex-col" style={{ minHeight: 0, height: "100%" }}>
                <div className="netbody-header">
                  <div className="flex items-center gap-2 ml-auto">
                    <Button variant="fill" icon={Plus} onClick={handleAddTerm} disabled={permLoading || !canModify || contractIsApproved} title={contractIsApproved ? "The agreement is approved and locked. Reopen it to change its terms." : !canModify ? NOT_AUTHORISED_MSG : undefined} data-testid="BTN_contract_term_create">
                    Create
                    </Button>
                  </div>
                </div>

                <div className="table-list flex-1 min-h-0">
                  <div className="table-list-scroll">
                    <table className="table-list-table">
                      <thead>
                        <tr>
                          <th className="table-list-th" style={{ width: 32 }} aria-hidden="true" />
                          <th className="table-list-th">Term ID</th>
                          <th className="table-list-th">Title</th>
                          <th className="table-list-th">Clause Ref</th>
                          <th className="table-list-th">Criticality</th>
                          <th className="table-list-th table-list-th--right">Actions</th>
                        </tr>
                      </thead>
                      <tbody aria-busy={termsLoading} aria-live="polite">
                        {termsLoading && (
                          <Skeleton.TableRows
                            cols={[
                              { width: "icon", align: "center" },
                              "2xs",
                              "md",
                              "sm",
                              "pill",
                              { width: "icon", align: "right" },
                            ]}
                          />
                        )}
                        {!termsLoading &&
                          paginatedTerms.map((term, idx) => {
                            const expanded = expandedTermId === term.id;
                            return (
                              <React.Fragment key={term.id}>
                                <tr
                                  className="table-list-row"
                                  onClick={() => handleTermCardClick(term)}
                                  data-testid={`BTN_contract_term_row_${term.id}`}
                                >
                                  <td className="table-list-cell" style={{ width: 32, textAlign: "center" }}>
                                    <span
                                      aria-hidden="true"
                                      style={{
                                        display: "inline-block",
                                        color: "#6b7280",
                                        fontSize: 12,
                                        transition: "transform 0.15s",
                                        transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                                      }}
                                    >
                                      ▶
                                    </span>
                                  </td>
                                  <td className="table-list-cell">
                                    <span
                                      style={{
                                        fontSize: 11,
                                        background: "#f3f4f6",
                                        color: "#6b7280",
                                        padding: "1px 6px",
                                        borderRadius: 4,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {selectedContract?.contract_id
                                        ? `${selectedContract.contract_id}-${String((termPage - 1) * termPageSize + idx + 1).padStart(3, "0")}`
                                        : String((termPage - 1) * termPageSize + idx + 1)}
                                    </span>
                                  </td>
                                  <td className="table-list-cell">
                                    <span className="table-list-cell-title">{term.title}</span>
                                  </td>
                                  <td className="table-list-cell">{term.clause_reference}</td>
                                  <td className="table-list-cell">
                                    <span className={severityBadgeCss(term.criticality)}>
                                      {term.criticality}
                                    </span>
                                  </td>
                                  <td className="table-list-cell table-list-cell--actions">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenConfig(term);
                                      }}
                                      className="table-list-action table-list-action--edit"
                                      title="Configure term"
                                      data-testid={`BTN_contract_term_${term.id}_config`}
                                    >
                                      <Edit2 size={14} />
                                    </button>
                                  </td>
                                </tr>

                                {expanded && (
                                  <tr style={{ background: "#f8fafc" }}>
                                    <td
                                      colSpan={6}
                                      style={{
                                        padding: "12px 16px 16px 48px",
                                        borderBottom: "1px solid #e5e7eb",
                                      }}
                                    >
                                      {term.description && (
                                        <p style={{ fontSize: 13, color: "#4b5563", marginBottom: 10 }}>
                                          {term.description}
                                        </p>
                                      )}

                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "flex-end",
                                          marginBottom: 8,
                                        }}
                                      >
                                        <Button
                                          variant="secondary-outline"
                                          icon={Link2}
                                          onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            loadTermAvailableSops(term.id);
                                            setShowTermLinkModal(true);
                                          }}
                                          disabled={permLoading || !canModify || contractIsApproved}
                                          title={contractIsApproved ? "The agreement is approved and locked. Reopen it to change its terms." : !canModify ? NOT_AUTHORISED_MSG : undefined}
                                          data-testid={`BTN_contract_term_${term.id}_link_sop`}
                                        >
                                          + Link SOP
                                        </Button>
                                      </div>

                                      {termLinksLoading ? (
                                        <p style={{ color: "#9ca3af", fontSize: 12, padding: "4px 0" }}>
                                          Loading linked SOPs…
                                        </p>
                                      ) : termLinkedSops.length === 0 ? (
                                        <p
                                          style={{
                                            color: "#9ca3af",
                                            fontSize: 12,
                                            fontStyle: "italic",
                                            padding: "4px 0",
                                          }}
                                        >
                                          No SOPs linked. Click "+ Link SOP" to add.
                                        </p>
                                      ) : (
                                        <ul
                                          style={{
                                            listStyleType: "disc",
                                            paddingLeft: 64,
                                            margin: 0,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 6,
                                          }}
                                        >
                                          {termLinkedSops.map((link) => (
                                            <li key={link.id} style={{ fontSize: 13, color: "#1f2937" }}>
                                              <div
                                                style={{
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  gap: 8,
                                                  width: "calc(100% - 8px)",
                                                  justifyContent: "space-between",
                                                }}
                                              >
                                                <span
                                                  role="button"
                                                  tabIndex={0}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSopProfileId(link.sop_id);
                                                  }}
                                                  onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                      e.preventDefault();
                                                      setSopProfileId(link.sop_id);
                                                    }
                                                  }}
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 8,
                                                    cursor: "pointer",
                                                    color: "#1d4ed8",
                                                    textDecoration: "underline",
                                                    textDecorationColor: "#bfdbfe",
                                                    textUnderlineOffset: "3px",
                                                  }}
                                                  title="Open SOP profile"
                                                >
                                                  {link.sop_code && (
                                                    <span
                                                      style={{
                                                        padding: "1px 6px",
                                                        borderRadius: 4,
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        background: "#dbeafe",
                                                        color: "#1d4ed8",
                                                      }}
                                                    >
                                                      {link.sop_code}
                                                    </span>
                                                  )}
                                                  <span style={{ fontWeight: 500 }}>{link.sop_name}</span>
                                                  {link.sop_description && (
                                                    <span style={{ color: "#6b7280", fontSize: 12 }}>
                                                      — {link.sop_description}
                                                    </span>
                                                  )}
                                                </span>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleUnlinkSopFromTerm(link.sop_id);
                                                  }}
                                                  className="table-list-action table-list-action--delete"
                                                  disabled={contractIsApproved}
                                                  title={contractIsApproved ? "The agreement is approved and locked. Reopen it to change its terms." : "Unlink"}
                                                  data-testid={`BTN_contract_term_sop_${link.sop_id}_unlink`}
                                                >
                                                  <Unlink size={12} />
                                                </button>
                                              </div>
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                  {!termsLoading && activeTerms.length === 0 && (
                    <NoDataFound title="No Terms Yet" message='Click "Create" to add one.' />
                  )}
                  <Pagination
                    page={termPage}
                    pageSize={termPageSize}
                    totalRecords={totalTermRecords}
                    onPageChange={setTermPage}
                    onPageSizeChange={setTermPageSize}
                    recordLabel="terms"
                  />
                </div>
              </div>
            )}
          </div>
        </Modal.Body>

        {/* Footer — details tab only */}
        {(!isEditMode || editTab === "details") && (
          <Modal.Footer
            className={isEditMode && selectedContract ? "app-modal-footer--split" : ""}
          >
            {/* LEFT: Active/Inactive toggle — edit mode only, hidden once
                approved+locked (approver acts via the workflow dot instead). */}
            {isEditMode && selectedContract && !contractIsApproved && (
              <Button
                variant={selectedContract.is_active ? "inactive-outline" : "active-outline"}
                icon={selectedContract.is_active ? CircleMinus : CircleCheck}
                onClick={handleToggleContractActive}
                disabled={permLoading || !canModify || togglingContract || saving}
                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                data-testid="BTN_contract_toggle_active"
              >
                {togglingContract
                  ? "..."
                  : selectedContract.is_active
                    ? "Inactive"
                    : "Active"}
              </Button>
            )}

            {/* RIGHT: Create mode → Submit; Edit read-only → Edit; Edit active → Update */}
            {!isEditMode && (
              <Button
                type="submit"
                form="contract-form"
                variant="fill"
                icon={Save}
                disabled={permLoading || !canModify || saving}
                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                data-testid="BTN_contract_submit"
              >
                {saving ? "Submitting..." : "Submit"}
              </Button>
            )}
            {isEditMode && !editingBasic && !contractIsApproved && (
              <Button
                variant="fill"
                icon={Edit2}
                onClick={() => setEditingBasic(true)}
                disabled={permLoading || !canModify || togglingContract}
                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                data-testid="BTN_contract_edit"
              >
                Edit
              </Button>
            )}
            {isEditMode && editingBasic && (
              <Button
                variant="update"
                icon={Check}
                disabled={permLoading || !canModify || saving || togglingContract}
                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                onClick={() => contractFormRef.current?.requestSubmit()}
                data-testid="BTN_contract_update"
              >
                {saving ? "Updating..." : "Update"}
              </Button>
            )}
          </Modal.Footer>
        )}
      </Modal>

      {/* ── Term Create / Edit Modal ─────────────────────────────── */}
      <Modal
        isOpen={showTermFormModal}
        onClose={() => setShowTermFormModal(false)}
        size="md"
      >
        <Modal.Header>
          {isTermEditMode && selectedTerm ? (
            <>
              Editing "
              <span className="item-highlight">{selectedTerm.title}</span>"
            </>
          ) : (
            "Create Term"
          )}
        </Modal.Header>
        <Modal.Body>
          <form
            id="term-form"
            onSubmit={handleTermSubmit}
            className="grid grid-cols-2 gap-x-6 gap-y-4"
          >
            <FormControl.Input
              label="Clause Reference"
              required
              data-testid="INP_term_clause"
              value={termFormData.clause_reference || ""}
              onChange={(e) =>
                setTermFormData({
                  ...termFormData,
                  clause_reference: e.target.value,
                })
              }
              placeholder="e.g. 12.3"
            />
            <FormControl.SearchSelect
              label="Criticality"
              data-testid="SEL_term_criticality"
              value={termFormData.criticality || "MEDIUM"}
              onChange={(value) =>
                setTermFormData({
                  ...termFormData,
                  criticality: String(value ?? "MEDIUM") as any,
                })
              }
              options={[
                { value: "HIGH", label: "High" },
                { value: "MEDIUM", label: "Medium" },
                { value: "LOW", label: "Low" },
              ]}
            />
            <ValidatedInput
              vh={vhTerm}
              name="term_title"
              code={109}
              label="Title"
              required
              className="col-span-2"
              data-testid="INP_term_title"
              value={termFormData.title || ""}
              onChange={(e) =>
                setTermFormData({ ...termFormData, title: e.target.value })
              }
              onAfterBlur={(v) => {
                if (!isUniqueLocal(955, v, activeTerms, isTermEditMode ? selectedTerm?.id : undefined)) {
                  vhTerm.setError("term_title", "A term with this title already exists for this agreement");
                }
              }}
            />
            <ValidatedTextarea
              vh={vhTerm}
              name="term_description"
              code={110}
              label="Description"
              className="col-span-2"
              data-testid="INP_term_description"
              value={termFormData.description || ""}
              onChange={(e) =>
                setTermFormData({
                  ...termFormData,
                  description: e.target.value,
                })
              }
              rows={4}
            />
          </form>
        </Modal.Body>
        <Modal.Footer
          className={
            isTermEditMode && selectedTerm ? "app-modal-footer--split" : ""
          }
        >
          {isTermEditMode && selectedTerm && (
            <Button
              variant={
                selectedTerm.is_active ? "inactive-outline" : "active-outline"
              }
              icon={selectedTerm.is_active ? CircleMinus : CircleCheck}
              onClick={handleToggleTermActive}
              disabled={permLoading || !canModify || togglingTerm || saving}
              title={!canModify ? NOT_AUTHORISED_MSG : undefined}
              data-testid="BTN_term_toggle_active"
            >
              {togglingTerm
                ? "..."
                : selectedTerm.is_active
                  ? "Inactive"
                  : "Active"}
            </Button>
          )}
          <Button
            type="submit"
            form="term-form"
            variant={isTermEditMode ? "update" : "fill"}
            icon={isTermEditMode ? Check : Save}
            disabled={permLoading || !canModify || saving || togglingTerm}
            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            data-testid="BTN_term_submit"
          >
            {saving
              ? isTermEditMode
                ? "Updating..."
                : "Submitting..."
              : isTermEditMode
                ? "Update"
                : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── 3-Column Term Configuration Modal ───────────────────── */}
      <Modal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        size="lg"
      >
        <div className="app-modal-header relative shrink-0">
          <div className="flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded border border-blue-200">
              Clause {activeTermForConfig?.clause_reference}
            </span>
            <span className="text-gray-400 text-xs">Term Configuration</span>
            {activeTermForConfig && (
              <button
                type="button"
                className="btn-primary-outline-none app-modal-header-icon"
                onClick={() => handleEditTerm(activeTermForConfig)}
                title="Edit Term"
                data-testid="BTN_contract_config_edit_term"
              >
                <Edit2 size={16} />
              </button>
            )}
          </div>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-1/3 max-w-md">
            <div className="bg-green-50 border-2 border-green-200 rounded-xl py-2 px-4 relative text-center shadow-sm">
              <div className="text-[10px] font-bold text-green-600 uppercase tracking-wider">
                Contract Term
              </div>
              <div className="text-base font-bold text-gray-800 leading-tight">
                {activeTermForConfig?.title}
              </div>
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white text-green-500 rounded-full border-2 border-green-200 p-0.5 z-10 shadow-sm">
                <ChevronDown size={14} strokeWidth={3} />
              </div>
            </div>
          </div>
          <div className="app-modal-header-right">
            <button
              type="button"
              onClick={() => setShowConfigModal(false)}
              className="app-modal-close"
              aria-label="Close modal"
              data-testid="BTN_contract_config_close"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 grid grid-cols-3 divide-x divide-gray-200 overflow-hidden min-h-0">
          <RisksColumn
            selectedControl={selectedControl}
            risks={risks}
            onRiskClick={handleRiskClick}
          />
          <ControlsColumn
            term={activeTermForConfig}
            controls={controls.filter(
              (c) => c.term_id === activeTermForConfig?.id,
            )}
            selectedControlId={selectedControl?.id}
            onSelectControl={setSelectedControl}
            onAddControl={handleAddControl}
          />
          <AttributesColumn
            selectedControl={selectedControl}
            attributes={attributes}
          />
        </div>
      </Modal>

      {/* ── Risk Visualization Modal ─────────────────────────────── */}
      <Modal
        isOpen={showRiskVisModal}
        onClose={() => setShowRiskVisModal(false)}
        size="lg"
      >
        <Modal.Header>Risk Visualization</Modal.Header>
        <Modal.Body>
          {selectedRiskForVis && (
            <RiskVisualization
              risk={{
                id: selectedRiskForVis.id,
                risk_code: `R-${selectedRiskForVis.id}`,
                risk_name: selectedRiskForVis.description,
              }}
              onClose={() => setShowRiskVisModal(false)}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* ── Link SOP to term modal ── */}
      <Modal
        isOpen={showTermLinkModal}
        onClose={() => setShowTermLinkModal(false)}
        size="sm"
      >
        <Modal.Header>
          Link SOP{
            expandedTermId && activeTerms.find((t) => t.id === expandedTermId)
              ? ` — ${activeTerms.find((t) => t.id === expandedTermId)!.title}`
              : ""
          }
        </Modal.Header>
        <Modal.Body>
          {termLinkError && (
            <div
              style={{
                marginBottom: "10px",
                padding: "8px 12px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "6px",
                color: "#b91c1c",
                fontSize: "12px",
              }}
            >
              {termLinkError}
            </div>
          )}
          <FormControl.SearchSelect
            label="Select SOP"
            data-testid="SEL_contract_link_sop"
            placeholder={
              termAvailableSops.length === 0
                ? "No SOPs available to link"
                : "Search and select an SOP…"
            }
            options={termAvailableSops.map((proc) => ({
              value: proc.id,
              label: proc.description
                ? `${proc.process_name} — ${proc.description}`
                : proc.process_name,
            }))}
            value={null}
            onChange={async (val) => {
              if (val == null) return;
              await handleLinkSopToTerm(Number(val));
              setShowTermLinkModal(false);
            }}
            disabled={termAvailableSops.length === 0}
          />
          {termAvailableSops.length === 0 && (
            <p
              style={{
                marginTop: "8px",
                fontSize: "12px",
                color: "#9ca3af",
                fontStyle: "italic",
              }}
            >
              Either all SOPs are already linked, or none exist for this entity
              yet. Create SOPs under Governance → SOPs first.
            </p>
          )}
        </Modal.Body>
      </Modal>

      {/* ── SOP profile overlay (mounted hidden; inner Modal portals above) ── */}
      {sopProfileId != null && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1500,
            pointerEvents: "none",
          }}
        >
          <div style={{ display: "none" }}>
            <GovernanceProcesses
              initialSopId={sopProfileId}
              onClose={() => setSopProfileId(null)}
            />
          </div>
        </div>
      )}

      <WorkflowModal
        isOpen={!!workflowRow}
        onClose={() => { setWorkflowRow(null); loadContracts(); }}
        kind={WF_KIND}
        id={workflowRow?.id ?? null}
        onChanged={loadContracts}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default GovernanceContracts;
